import { createReducer, on } from '@ngrx/store';
import {
  AutoPairGlobalConfiguration,
  AutoPairTransitProgramConfiguration,
  OperationalFootprintConfiguration,
  TransitProgramInfo
} from '@uzurv/clara';
import { OperationalFootprintInfo } from 'projects/sdk/packages/clara/src/models/operational-footprint';
import * as AutopairingActions from './autopairing.actions';

export const autopairingFeatureKey = 'autopairing';

export interface GlobalConfig {
  config?: AutoPairGlobalConfiguration;
  loading?: boolean;
  formValues?: AutoPairGlobalConfiguration;
}

export interface OperationalFootprintState {
  operationalFootprints: Array<OperationalFootprintInfo>;
  pageNumber: number;
  pageSize: number;
  shouldShowOperationalFootprintLoader: boolean;
  selectedOperationalFootprint: OperationalFootprintInfo;
}

export interface OperationalFootprintConfig {
  config?: OperationalFootprintConfiguration;
  loading?: boolean;
  formValues?: OperationalFootprintConfiguration;
}

export interface TransitProgramSearchState {
  transitPrograms: Array<TransitProgramInfo>;
  transitProgramsPageIndex: number;
  transitProgramsPageSize: number;
  searchString: string;
  shouldShowTransitProgramsLoader: boolean;
  selectedTransitProgram: TransitProgramInfo;
}

export interface TransitProgramConfig {
  config?: AutoPairTransitProgramConfiguration;
  loading?: boolean;
  formValues?: AutoPairTransitProgramConfiguration;
}

export interface AutopairingState {
  global: GlobalConfig;
  operationalFootprintConfig: OperationalFootprintConfig;
  transitProgramConfig: TransitProgramConfig;
  tabIndex?: number;
  operationalFootprintState: OperationalFootprintState;
  transitProgramSearchState: TransitProgramSearchState;
}

export const initialState: AutopairingState = {
  global: {
    config: null,
    loading: false,
    formValues: null
  },
  operationalFootprintConfig: {
    config: null,
    loading: null,
    formValues: null
  },
  transitProgramConfig: {
    config: null,
    loading: null,
    formValues: null
  },
  tabIndex: 0,
  operationalFootprintState: {
    operationalFootprints: null,
    pageNumber: 0,
    pageSize: 10000,
    shouldShowOperationalFootprintLoader: false,
    selectedOperationalFootprint: null
  },
  transitProgramSearchState: {
    transitPrograms: null,
    transitProgramsPageIndex: 0,
    transitProgramsPageSize: 20,
    searchString: '',
    shouldShowTransitProgramsLoader: false,
    selectedTransitProgram: null
  }
};

export const autopairing = createReducer(
  initialState,

  on(AutopairingActions.GetAutopairingGlobalConfig, AutopairingActions.SaveAutopairingGlobalConfig, (state, action) => {
    return { ...state, global: { ...state.global, loading: true } };
  }),

  on(AutopairingActions.GetAutopairingGlobalConfigSuccess, AutopairingActions.SaveAutopairingGlobalConfigSuccess, (state, action) => {
    return { ...state, global: { ...state.global, config: action.config, loading: false } };
  }),

  on(AutopairingActions.GetAutopairingGlobalConfigFail, (state, action) => {
    return { ...state, global: { ...state.global, config: null, loading: false } }; // Reset config on failure
  }),

  on(AutopairingActions.SaveAutopairingGlobalConfigFail, (state, action) => {
    return { ...state, global: { ...state.global, loading: false } }; // Reset config on failure
  }),

  on(AutopairingActions.StoreAutopairingGlobalFormValues, (state, action) => {
    return { ...state, global: { ...state.global, formValues: action.formValues } };
  }),

  on(AutopairingActions.RestoreState, (state, action) => {
    return { ...action.state };
  }),

  on(AutopairingActions.ClearState, (state, action) => {
    return { ...initialState };
  }),

  on(AutopairingActions.SetAutopairingTabIndex, (state, action) => {
    return { ...state, tabIndex: action.index };
  }),

  // Operational Footprint

  on(AutopairingActions.GetOperationalFootprints, (state, action) => {
    return {
      ...state,
      operationalFootprintState: {
        ...state.operationalFootprintState,
        shouldShowOperationalFootprintLoader: true
      }
    };
  }),

  on(AutopairingActions.GetOperationalFootprintsSuccess, (state, action) => {
    return {
      ...state,
      operationalFootprintState: {
        ...state.operationalFootprintState,
        operationalFootprints: action.operationalFootprints,
        shouldShowOperationalFootprintLoader: false
      }
    };
  }),

  on(AutopairingActions.GetOperationalFootprintsFail, (state, action) => {
    return {
      ...state,
      operationalfootprintState: {
        ...state.operationalFootprintState,
        shouldShowOperationalFootprintLoader: false
      }
    };
  }),

  on(AutopairingActions.SelectOperationalFootprint, (state, action) => {
    return {
      ...state,
      operationalFootprintState: {
        ...state.operationalFootprintState,
        selectedOperationalFootprint: action.selectedOperationalFootprint
      }
    };
  }),

  on(AutopairingActions.ClearSelectedOperationalFootprint, state => {
    return {
      ...state,
      operationalFootprintState: {
        ...state.operationalFootprintState,
        selectedOperationalFootprint: initialState.operationalFootprintState.selectedOperationalFootprint
      }
    };
  }),

  on(AutopairingActions.GetOperationalFootprintConfig, AutopairingActions.SaveAutopairingOperationalFootprintConfig, (state, action) => {
    return { ...state, operationalFootprintConfig: { ...state.operationalFootprintConfig, loading: true } };
  }),

  on(
    AutopairingActions.GetOperationalFootprintConfigSuccess,
    AutopairingActions.SaveAutopairingOperationalFootprintConfigSuccess,
    (state, action) => {
      return { ...state, operationalFootprintConfig: { ...state.operationalFootprintConfig, config: action.config, loading: false } };
    }
  ),

  on(AutopairingActions.GetOperationalFootprintConfigFail, (state, action) => {
    return { ...state, operationalFootprintConfig: { ...state.operationalFootprintConfig, config: null, loading: false } };
  }),

  on(AutopairingActions.SaveAutopairingOperationalFootprintConfigFail, (state, action) => {
    return { ...state, operationalFootprintConfig: { ...state.operationalFootprintConfig, config: null, loading: false } };
  }),

  on(AutopairingActions.StoreAutopairingOperationalFootprintFormValues, (state, action) => {
    return { ...state, operationalFootprintConfig: { ...state.operationalFootprintConfig, formValues: action.formValues } };
  }),

  on(AutopairingActions.ClearAutopairingOperationalFootprintFormValues, state => {
    return { ...state, operationalFootprintConfig: initialState.operationalFootprintConfig };
  }),

  // Transit Program Search

  on(AutopairingActions.GetTransitPrograms, state => {
    return {
      ...state,
      transitProgramSearchState: {
        ...state.transitProgramSearchState,
        shouldShowTransitProgramsLoader: true
      }
    };
  }),

  on(AutopairingActions.GetTransitProgramsSuccess, (state, action) => {
    return {
      ...state,
      transitProgramSearchState: {
        ...state.transitProgramSearchState,
        transitPrograms: action.transitPrograms,
        shouldShowTransitProgramsLoader: false
      }
    };
  }),

  on(AutopairingActions.GetTransitProgramsFail, state => {
    return {
      ...state,
      transitProgramSearchState: {
        ...state.transitProgramSearchState,
        shouldShowTransitProgramsLoader: false
      }
    };
  }),

  on(AutopairingActions.PaginateTransitPrograms, (state, action) => {
    return {
      ...state,
      transitProgramSearchState: {
        ...state.transitProgramSearchState,
        transitProgramsPageIndex: action.pageEvent.pageIndex,
        transitProgramsPageSize: action.pageEvent.pageSize,
        shouldShowTransitProgramsLoader: true
      }
    };
  }),

  on(AutopairingActions.ClearSearch, state => {
    return {
      ...state,
      transitProgramSearchState: {
        ...state.transitProgramSearchState,
        transitProgramsPageIndex: 0,
        transitProgramsPageSize: 20,
        searchString: '',
        shouldShowTransitProgramsLoader: true
      }
    };
  }),

  on(AutopairingActions.SearchTransitPrograms, (state, action) => {
    return {
      ...state,
      transitProgramSearchState: {
        ...state.transitProgramSearchState,
        transitProgramsPageIndex:
          state.transitProgramSearchState.searchString?.length > 0
            ? state.transitProgramSearchState.transitProgramsPageIndex
            : initialState.transitProgramSearchState.transitProgramsPageIndex,
        transitProgramsPageSize:
          state.transitProgramSearchState.searchString?.length > 0
            ? state.transitProgramSearchState.transitProgramsPageSize
            : initialState.transitProgramSearchState.transitProgramsPageSize,
        searchString: action.searchString,
        shouldShowTransitProgramsLoader: true
      }
    };
  }),

  on(AutopairingActions.SearchTransitProgramsSuccess, (state, action) => {
    return {
      ...state,
      transitProgramSearchState: {
        ...state.transitProgramSearchState,
        transitPrograms: action.transitPrograms,
        shouldShowTransitProgramsLoader: false
      }
    };
  }),

  on(AutopairingActions.SearchTransitProgramsFail, state => {
    return {
      ...state,
      transitProgramSearchState: {
        ...state.transitProgramSearchState,
        shouldShowTransitProgramsLoader: false
      }
    };
  }),

  on(AutopairingActions.SelectTransitProgram, (state, action) => {
    return {
      ...state,
      transitProgramSearchState: {
        ...state.transitProgramSearchState,
        selectedTransitProgram: action.selectedTransitProgram
      }
    };
  }),

  on(AutopairingActions.ClearSelectedTransitProgram, state => {
    return {
      ...state,
      transitProgramSearchState: {
        ...state.transitProgramSearchState,
        selectedTransitProgram: initialState.transitProgramSearchState.selectedTransitProgram
      }
    };
  }),

  // Transit Program Configuration

  on(AutopairingActions.GetAutopairingTransitProgramConfig, AutopairingActions.SaveAutopairingTransitProgramConfig, (state, action) => {
    return { ...state, transitProgramConfig: { ...state.transitProgramConfig, loading: true } };
  }),

  on(
    AutopairingActions.GetAutopairingTransitProgramConfigSuccess,
    AutopairingActions.SaveAutopairingTransitProgramConfigSuccess,
    (state, action) => {
      return { ...state, transitProgramConfig: { ...state.transitProgramConfig, config: action.config, loading: false } };
    }
  ),

  on(AutopairingActions.GetAutopairingTransitProgramConfigFail, (state, action) => {
    return { ...state, transitProgramConfig: { ...state.transitProgramConfig, config: null, loading: false } };
  }),

  on(AutopairingActions.SaveAutopairingTransitProgramConfigFail, (state, action) => {
    return { ...state, transitProgramConfig: { ...state.transitProgramConfig, loading: false } };
  }),

  on(AutopairingActions.StoreAutopairingTransitProgramFormValues, (state, action) => {
    return { ...state, transitProgramConfig: { ...state.transitProgramConfig, formValues: action.formValues } };
  }),

  on(AutopairingActions.ClearAutopairingTransitProgramFormValues, state => {
    return { ...state, transitProgramConfig: initialState.transitProgramConfig };
  })
);

export const autopairingReducer = (state: AutopairingState | undefined, action: any) => {
  return autopairing(state, action);
};
